import {
  ActionIcon,
  Button,
  FileButton,
  Group,
  Image,
  Input,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { IconPhoto, IconTrash, IconUpload } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { useRef } from 'react';

type LogoUploaderProps = Omit<React.ComponentProps<typeof Stack>, 'onSelect'> & {
  previewUrl?: string;
  onSelect: (file: File) => void;
  onRemove: () => void;
};

export const LogoUploader = ({
  previewUrl,
  onSelect,
  onRemove,
  ...stackProps
}: LogoUploaderProps) => {
  const { t } = useTranslation();
  const resetRef = useRef<() => void>(null);

  const handleSelect = (file: File | null) => {
    if (file) onSelect(file);
  };

  const handleRemove = () => {
    resetRef.current?.();
    onRemove();
  };

  return (
    <Stack gap="xs" w={200} h="100%" justify="space-between" {...stackProps}>
      <Input.Wrapper label={t(i18nKeys.FORM.INVOICING.YOUR_LOGO)}>
        <Paper
          withBorder
          radius="md"
          p="sm"
          h={150}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {previewUrl ? (
            <Image src={previewUrl} alt="" fit="contain" mah={120} />
          ) : (
            <Stack align="center" gap={4} c="dimmed" px="sm">
              <IconPhoto size={34} stroke={1.3} />
              <Text size="xs" ta="center">
                {t(i18nKeys.FORM.INVOICING.IMPORT_LOGO)}
              </Text>
            </Stack>
          )}
        </Paper>
      </Input.Wrapper>
      <Group gap="xs" mt="lg" justify="center">
        <FileButton onChange={handleSelect} accept="image/png,image/jpeg" resetRef={resetRef}>
          {props => (
            <Button {...props} variant="light" size="xs" leftSection={<IconUpload size={16} />}>
              {t(i18nKeys.FORM.USER_SETTINGS.UPLOAD_BUTTON)}
            </Button>
          )}
        </FileButton>
        {previewUrl && (
          <ActionIcon
            variant="light"
            color="red"
            size={30}
            onClick={handleRemove}
            aria-label={t(i18nKeys.DELETE)}
          >
            <IconTrash size={16} stroke={1.5} />
          </ActionIcon>
        )}
      </Group>
    </Stack>
  );
};
